'use client';

import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Text } from '~/shared/components/Text/Text';

function countWrappedLines(element: HTMLElement): number {
    const style = getComputedStyle(element);
    const lineHeight = parseFloat(style.lineHeight);
    const lineHeightPx =
        Number.isFinite(lineHeight) && lineHeight > 0
            ? lineHeight
            : parseFloat(style.fontSize) * 1.2;
    const height = element.getBoundingClientRect().height;
    return Math.max(1, Math.round(height / lineHeightPx));
}

export type PageHeroHeadlineProps = {
    headline: string;
    color: string;
};

export function PageHeroHeadline({ headline, color }: PageHeroHeadlineProps) {
    // Performance hit is very cheap: one hidden measure layer per hero, ResizeObserver only on real size changes.
    const measureRef = useRef<HTMLDivElement>(null);
    const [compact, setCompact] = useState(false);

    const evaluate = useCallback(() => {
        const root = measureRef.current;
        const textEl = root?.firstElementChild as HTMLElement | undefined;
        if (!textEl) return;
        setCompact(countWrappedLines(textEl) >= 3);
    }, []);

    useLayoutEffect(() => {
        evaluate();
    }, [headline, evaluate]);

    useLayoutEffect(() => {
        const root = measureRef.current;
        if (!root) return;
        const observer = new ResizeObserver(() => evaluate());
        observer.observe(root);
        return () => observer.disconnect();
    }, [evaluate]);

    const sharedCss = { wordBreak: 'break-word' as const, textTransform: 'uppercase' as const };

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            <div
                ref={measureRef}
                aria-hidden
                style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 0,
                    visibility: 'hidden',
                    pointerEvents: 'none',
                    width: '100%',
                }}
            >
                <Text variant="display0" as="span" color={color} css={sharedCss}>
                    {headline}
                </Text>
            </div>
            <Text
                variant={compact ? 'display1' : 'display0'}
                as="h1"
                color={color}
                css={sharedCss}
                data-testid="page-hero-headline"
            >
                {headline}
            </Text>
        </div>
    );
}
